// src/pages/admin/Dashboard.js
import React, { useEffect, useState } from "react";
import Layout from "../../component/Layout.js";
import { Helmet } from "react-helmet-async";
import api from "../../axios.js";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const Dashboard = () => {
  const [licenses, setLicenses] = useState([]);
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [softwares, setSoftwares] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const isAdmin = sessionStorage.getItem("isAdmin") === "true";
    if (!isAdmin) {
      navigate("/dashboard");
      return;
    }
    const fetchAll = async () => {
      try {
        const [licenseRes, deviceRes, userRes, softwareRes] = await Promise.all([
          api.get("/api/license"),
          api.get("/api/device"),
          api.get("/api/users"),
          api.get("/api/software"),
        ]);
        setLicenses(licenseRes.data);
        setDevices(deviceRes.data);
        setUsers(userRes.data);
        setSoftwares(softwareRes.data);
      } catch (error) {
        console.error("Dashboard verileri alinamadi:", error);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        }
      }
    };

    fetchAll();
  }, [navigate]);

  const activeLicenses = licenses.filter((l) => l.isActive);
  const inactiveLicenses = licenses.filter((l) => !l.isActive);
  const unapprovedDevices = devices.filter((d) => !d.isApproved);

  const licenseChartData = softwares.map((s) => {
    const licenseCount = licenses.filter((l) => l.softwareId === s.id).length;
    return { name: s.name, Lisans: licenseCount };
  });

  return (
    <div>
      <Helmet>
        <title>Yönetim Paneli</title>
      </Helmet>
      <Layout pageTitle="Yönetim Paneli">
        {/* Hızlı Aksiyonlar */}
        <div className="flex flex-wrap gap-4 mb-6">
          <button
            onClick={() => navigate("/admin/lisans")}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            Lisans Listesi
          </button>
          <button
            onClick={() => navigate("/admin/users")}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Kullanıcılar
          </button>
          <button
            onClick={() => navigate("/admin/cihaz")}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Cihazlar
          </button>
          <button
            onClick={() => navigate("/admin/paket")}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            Yazılımlar
          </button>
        </div>

        {/* Sayısal Kartlar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard title="Toplam Lisans" count={licenses.length} color="indigo" />
          <StatCard title="Aktif Lisans" count={activeLicenses.length} color="green" />
          <StatCard title="Pasif Lisans" count={inactiveLicenses.length} color="red" />
          <StatCard title="Kullanıcı Sayısı" count={users.length} color="blue" />
          <StatCard title="Cihaz Sayısı" count={devices.length} color="yellow" />
          <StatCard title="Onaysız Cihaz" count={unapprovedDevices.length} color="orange" />
          <StatCard title="Yazılım Sayısı" count={softwares.length} color="purple" />
        </div>

        {/* Grafik Alanı */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-semibold mb-4">Yazılım Bazlı Lisans Sayıları</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={licenseChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="Lisans" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Son 5 Lisans */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Yakında Süresi Dolacak Lisanslar</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Kullanıcı</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Yazılım</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Paket</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Son Tarih</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Durum</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {licenses
                  .sort((a, b) => new Date(a.endDate) - new Date(b.endDate))
                  .slice(0, 5)
                  .map((license) => (
                    <tr key={license.id}>
                      <td className="px-4 py-2">{license.userFullName || license.email}</td>
                      <td className="px-4 py-2">{license.softwareName}</td>
                      <td className="px-4 py-2">{license.packageName}</td>
                      <td className="px-4 py-2">{new Date(license.endDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${license.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                        >
                          {license.isActive ? "Aktif" : "Pasif"}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    </div>
  );
};

const StatCard = ({ title, count, color }) => (
  <div className={`bg-white p-4 rounded-lg shadow border-t-4 border-${color}-500`}>
    <h3 className="text-sm font-semibold text-gray-500 mb-1">{title}</h3>
    <p className={`text-2xl font-bold text-${color}-600`}>{count}</p>
  </div>
);

export default Dashboard;
